import React, {useCallback, useMemo} from 'react';
import {Box} from '@tempo/core';
import Select from '@components/Select';
import {useTranslation} from 'next-i18next';
import {useRouter} from 'next/router';
import useUserSetting from '@deezer/next/shared/hooks/useUserSetting';

import {
	availableLanguages,
	languagesInfo,
} from '@deezer/locales/d4c/metas.json';
import {
	getLanguagePreference,
	setLanguagePreference,
} from '@deezer/next/services/storage/shared/effects';

type LanguageKey = keyof typeof languagesInfo;
type LanguageInfo = (typeof languagesInfo)[LanguageKey];

const typedAvailableLanguages = availableLanguages as LanguageKey[];
const fallbackLanguage: LanguageKey = 'en';

const isLanguageKey = (value: string): value is LanguageKey =>
	value in languagesInfo;

const getLanguageInfo = (language: string): LanguageInfo => {
	return isLanguageKey(language)
		? languagesInfo[language]
		: languagesInfo[fallbackLanguage];
};

const UserLanguage = () => {
	const {t, i18n} = useTranslation('d4c');
	const router = useRouter();
	const {setting: languageSetting, setSetting} = useUserSetting(
		'user',
		'language',
	);

	const currentLanguageValue = useMemo<LanguageKey>(() => {
		const language =
			languageSetting ||
			getLanguagePreference() ||
			i18n.language ||
			fallbackLanguage;

		return isLanguageKey(language) ? language : fallbackLanguage;
	}, [i18n.language, languageSetting]);

	const currentLanguageInfo = useMemo(() => {
		return getLanguageInfo(currentLanguageValue);
	}, [currentLanguageValue]);

	const languageOptions = useMemo(() => {
		return typedAvailableLanguages.map(lang => ({
			label: languagesInfo[lang].name,
			value: lang,
		}));
	}, []);

	const handleLanguageChange = useCallback(
		async (value: string) => {
			if (!isLanguageKey(value) || value === currentLanguageValue) return;

			const {pathname, asPath, query} = router;
			const newLocale = value;

			setLanguagePreference(newLocale);
			await setSetting(newLocale);

			document.cookie = `NEXT_LOCALE=${newLocale}; path=/`;
			await router.replace({pathname, query}, asPath, {locale: newLocale});
		},
		[currentLanguageValue, router, setSetting],
	);

	return (
		<Box>
			<Select
				options={languageOptions}
				value={currentLanguageValue}
				onChange={handleLanguageChange}
				placeholder={
					currentLanguageInfo.name ||
					t('profilesettings_text_selectlanguage_d4c-web')
				}
			/>
		</Box>
	);
};

export default UserLanguage;
